import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { issueFullTaxInvoice } from "src/api/FlowAccount";
import { BuyerErrors, BuyerFormValues, normaliseTaxId, validateBuyer } from "./thaiTaxId";

/**
 * Buyer details for a full tax invoice (ใบกำกับภาษี/ใบเสร็จรับเงิน).
 *
 * Name, address, head office or branch number, and a 13-digit tax id with a
 * valid check digit — what the Revenue Department requires on a full tax
 * invoice. The details go to FlowAccount only; Book and Go does not keep them.
 */

const INK = "#22356D";

const fieldSx = {
  "& .MuiInputBase-root": { fontFamily: "var(--font-regular)", fontSize: "13px", color: INK, borderRadius: "8px" },
  "& .MuiInputLabel-root": { fontFamily: "var(--font-regular)", fontSize: "13px" },
};

const EMPTY: BuyerFormValues = {
  name: "",
  tax_id: "",
  branch_type: "head_office",
  branch_no: "",
  address: "",
  zip_code: "",
  phone: "",
  email: "",
};

interface Props {
  locationId: number;
  pushId: number;
  onDone: (result: { push_id: number; document_serial: string | null }) => void;
  onCancel: () => void;
}

const FullTaxInvoiceForm = ({ locationId, pushId, onDone, onCancel }: Props) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<BuyerFormValues>(EMPTY);
  const [errors, setErrors] = useState<BuyerErrors>({});
  const [serverError, setServerError] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const set = (key: keyof BuyerFormValues) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = async () => {
    const found = validateBuyer(values);
    setErrors(found);
    setServerError("");
    if (Object.keys(found).length) return;
    setSubmitting(true);
    try {
      const result = await issueFullTaxInvoice(locationId, pushId, {
        name: values.name.trim(),
        tax_id: normaliseTaxId(values.tax_id),
        branch: values.branch_type === "head_office" ? "head_office" : values.branch_no.trim(),
        address: values.address.trim(),
        zip_code: values.zip_code.trim() || undefined,
        phone: values.phone.trim() || undefined,
        email: values.email.trim() || undefined,
      });
      onDone(result);
    } catch (error: any) {
      setServerError(typeof error === "string" ? error : t("flowaccount.errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography sx={{ fontFamily: "var(--font-semibold)", fontSize: "14px", color: INK }}>
        {t("flowaccount.buyerDetails")}
      </Typography>
      <TextField
        id="fa-buyer-name"
        label={t("flowaccount.buyerName")}
        size="small"
        value={values.name}
        onChange={set("name")}
        error={!!errors.name}
        helperText={errors.name ? t("flowaccount.errorRequired") : ""}
        sx={fieldSx}
      />
      <TextField
        id="fa-buyer-tax-id"
        label={t("flowaccount.taxId")}
        size="small"
        value={values.tax_id}
        onChange={set("tax_id")}
        error={!!errors.tax_id}
        helperText={errors.tax_id ? t("flowaccount.errorTaxId") : ""}
        inputProps={{ inputMode: "numeric", maxLength: 17 }}
        sx={fieldSx}
      />
      <RadioGroup
        row
        value={values.branch_type}
        onChange={(e) => setValues((v) => ({ ...v, branch_type: e.target.value as BuyerFormValues["branch_type"] }))}
      >
        <FormControlLabel
          value="head_office"
          control={<Radio size="small" />}
          label={t("flowaccount.headOffice")}
          sx={{ "& .MuiTypography-root": { fontFamily: "var(--font-regular)", fontSize: "13px", color: INK } }}
        />
        <FormControlLabel
          value="branch"
          control={<Radio size="small" />}
          label={t("flowaccount.branch")}
          sx={{ "& .MuiTypography-root": { fontFamily: "var(--font-regular)", fontSize: "13px", color: INK } }}
        />
      </RadioGroup>
      {values.branch_type === "branch" && (
        <TextField
          id="fa-buyer-branch-no"
          label={t("flowaccount.branchNo")}
          size="small"
          value={values.branch_no}
          onChange={set("branch_no")}
          error={!!errors.branch}
          helperText={errors.branch ? t("flowaccount.errorBranch") : ""}
          inputProps={{ inputMode: "numeric", maxLength: 5 }}
          sx={fieldSx}
        />
      )}
      <TextField
        id="fa-buyer-address"
        label={t("flowaccount.address")}
        size="small"
        multiline
        minRows={2}
        value={values.address}
        onChange={set("address")}
        error={!!errors.address}
        helperText={errors.address ? t("flowaccount.errorRequired") : ""}
        sx={fieldSx}
      />
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField id="fa-buyer-zip" label={t("flowaccount.zipCode")} size="small" value={values.zip_code} onChange={set("zip_code")} sx={{ ...fieldSx, flex: 1 }} />
        <TextField id="fa-buyer-phone" label={t("flowaccount.phone")} size="small" value={values.phone} onChange={set("phone")} sx={{ ...fieldSx, flex: 1 }} />
      </Box>
      <TextField id="fa-buyer-email" label={t("flowaccount.email")} size="small" value={values.email} onChange={set("email")} sx={fieldSx} />

      {serverError && (
        <Typography sx={{ fontFamily: "var(--font-regular)", fontSize: "12px", color: "#DC2626" }}>{serverError}</Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button onClick={onCancel} disabled={submitting} sx={{ textTransform: "none", color: "#64748b", fontFamily: "var(--font-medium)" }}>
          {t("flowaccount.cancel")}
        </Button>
        <Button
          onClick={submit}
          disabled={submitting}
          sx={{
            textTransform: "none",
            fontFamily: "var(--font-semibold)",
            fontSize: "13px",
            color: "white",
            background: INK,
            borderRadius: "8px",
            px: 2.5,
            "&:hover": { background: "#1a2956" },
            "&:disabled": { background: "#D1D5DB", color: "white" },
          }}
        >
          {submitting ? t("flowaccount.issuing") : t("flowaccount.issueFull")}
        </Button>
      </Box>
    </Box>
  );
};

export default FullTaxInvoiceForm;
